import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useJsApiLoader } from "@react-google-maps/api";
import { Input } from "antd";
import { GOOGLE_MAPS_CONFIG } from "../../config/googleMaps";
import {
  locationCoord,
  parseStoredLocation,
} from "../../utils/organizationLocation";

const GOOGLE_MAPS_LIBRARIES = ["places"];
const DETAIL_FIELDS = ["formatted_address", "geometry", "name"];

const toLocationValue = (name, lat, lng) => ({
  name,
  lat: locationCoord(lat),
  lng: locationCoord(lng),
});

const getPlacesAttributionNode = () => {
  let node = document.getElementById("organization-location-attribution");
  if (!node) {
    node = document.createElement("div");
    node.id = "organization-location-attribution";
    node.setAttribute("aria-hidden", "true");
    node.style.display = "none";
    document.body.appendChild(node);
  }
  return node;
};

const mapLegacyPredictions = (results = []) =>
  results.map((prediction) => ({
    id: prediction.place_id,
    label: prediction.description,
    prediction,
  }));

const mapNewPredictions = (suggestions = []) =>
  suggestions
    .map((item) => item.placePrediction)
    .filter(Boolean)
    .map((prediction) => ({
      id: prediction.placeId,
      label:
        prediction.text?.toString?.() ||
        prediction.text?.text ||
        prediction.text ||
        "",
      prediction,
    }));

export function OrganizationLocationInput({ value, onChange, id, onBlur }) {
  const onChangeRef = useRef(onChange);
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const requestIdRef = useRef(0);
  const wrapRef = useRef(null);
  const displayName = parseStoredLocation(value)?.name || "";
  const [text, setText] = useState(displayName);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [menuRect, setMenuRect] = useState(null);

  onChangeRef.current = onChange;

  const { isLoaded, loadError } = useJsApiLoader({
    id: "unzippd-google-maps-script",
    googleMapsApiKey: GOOGLE_MAPS_CONFIG.apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (document.activeElement && wrapRef.current?.contains(document.activeElement)) {
      return;
    }
    setText(displayName);
  }, [displayName]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return undefined;
    }

    const updateMenuRect = () => {
      const anchor = wrapRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      setMenuRect({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updateMenuRect();
    window.addEventListener("resize", updateMenuRect);
    document.addEventListener("scroll", updateMenuRect, true);

    return () => {
      window.removeEventListener("resize", updateMenuRect);
      document.removeEventListener("scroll", updateMenuRect, true);
    };
  }, [open, suggestions, statusText]);

  useEffect(
    () => () => {
      window.clearTimeout(debounceRef.current);
    },
    [],
  );

  const measureAnchor = () => {
    const anchor = wrapRef.current;
    if (!anchor) return null;
    const rect = anchor.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    };
  };

  const getSessionToken = () => {
    const places = window.google?.maps?.places;
    if (!places?.AutocompleteSessionToken) return undefined;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const fetchLegacyPredictions = (query) =>
    new Promise((resolve) => {
      const places = window.google?.maps?.places;
      if (!places?.AutocompleteService) {
        resolve([]);
        return;
      }

      const service = new places.AutocompleteService();
      const request = { input: query };
      const sessionToken = getSessionToken();
      if (sessionToken) request.sessionToken = sessionToken;

      service.getPlacePredictions(request, (results, status) => {
        if (status !== places.PlacesServiceStatus.OK || !results?.length) {
          resolve([]);
          return;
        }
        resolve(mapLegacyPredictions(results));
      });
    });

  const fetchSuggestions = useCallback(async (query) => {
    const trimmed = query.trim();
    const requestId = ++requestIdRef.current;
    const places = window.google?.maps?.places;

    if (!trimmed || !places) {
      if (requestId !== requestIdRef.current) return;
      setSuggestions([]);
      setOpen(Boolean(trimmed));
      setStatusText(trimmed ? "Location search is unavailable" : "");
      return;
    }

    setOpen(true);
    setStatusText("Searching...");

    try {
      let mapped = await fetchLegacyPredictions(trimmed);

      if (!mapped.length && places.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
        const request = { input: trimmed };
        const sessionToken = getSessionToken();
        if (sessionToken) request.sessionToken = sessionToken;

        const { suggestions: nextSuggestions } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request,
          );
        mapped = mapNewPredictions(nextSuggestions);
      }

      if (requestId !== requestIdRef.current) return;

      setSuggestions(mapped);
      setStatusText(mapped.length ? "" : "No locations found");
    } catch {
      if (requestId !== requestIdRef.current) return;
      setSuggestions([]);
      setStatusText("No locations found");
    }
  }, []);

  const commitLocation = (nextValue) => {
    setText(nextValue.name);
    setSuggestions([]);
    setOpen(false);
    setStatusText("");
    onChangeRef.current?.(nextValue);
  };

  const handleSelect = async (item) => {
    const places = window.google?.maps?.places;
    if (!places) return;

    try {
      if (item.prediction?.toPlace) {
        const place = item.prediction.toPlace();
        await place.fetchFields({
          fields: ["formattedAddress", "location"],
        });
        sessionTokenRef.current = null;
        commitLocation(
          toLocationValue(
            place.formattedAddress || item.label,
            place.location?.lat(),
            place.location?.lng(),
          ),
        );
        return;
      }

      const service = new places.PlacesService(getPlacesAttributionNode());
      const request = {
        placeId: item.id,
        fields: DETAIL_FIELDS,
      };
      const sessionToken = getSessionToken();
      if (sessionToken) request.sessionToken = sessionToken;

      service.getDetails(request, (place, status) => {
        sessionTokenRef.current = null;

        if (status !== places.PlacesServiceStatus.OK || !place) {
          commitLocation(toLocationValue(item.label, null, null));
          return;
        }

        commitLocation(
          toLocationValue(
            place.formatted_address || place.name || item.label,
            place.geometry?.location?.lat(),
            place.geometry?.location?.lng(),
          ),
        );
      });
    } catch {
      commitLocation(toLocationValue(item.label, null, null));
    }
  };

  const handleChange = (event) => {
    const nextText = event.target.value;
    setText(nextText);

    if (!nextText) {
      requestIdRef.current += 1;
      setSuggestions([]);
      setOpen(false);
      setStatusText("");
      onChangeRef.current?.(null);
      return;
    }

    setOpen(true);
    setMenuRect(measureAnchor());
    setStatusText("Searching...");
    onChangeRef.current?.(toLocationValue(nextText, null, null));

    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchSuggestions(nextText);
    }, 150);
  };

  if (loadError) {
    return (
      <Input status="error" disabled placeholder="Unable to load locations" />
    );
  }

  if (!isLoaded) {
    return <Input disabled placeholder="Loading locations..." />;
  }

  const menu =
    open && menuRect
      ? createPortal(
          <ul
            className="organization-location-suggestions m-0 list-none overflow-auto rounded-lg border border-[#d9d9d9] bg-white p-0 shadow-lg"
            style={{
              position: "fixed",
              top: menuRect.top,
              left: menuRect.left,
              width: menuRect.width,
              maxHeight: 240,
              zIndex: 11000,
            }}
          >
            {suggestions.length ? (
              suggestions.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="block w-full cursor-pointer border-0 bg-transparent px-3 py-2 text-left text-sm text-black hover:bg-[#f5f5f5]"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(item)}
                  >
                    {item.label}
                  </button>
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-sm text-gray-500">
                {statusText || "Type to search locations"}
              </li>
            )}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div ref={wrapRef} className="relative w-full">
      <Input
        id={id}
        value={text}
        placeholder="Search organisation location"
        autoComplete="off"
        onChange={handleChange}
        onFocus={() => {
          if (suggestions.length || statusText) {
            setOpen(true);
            setMenuRect(measureAnchor());
          } else if (text.trim()) {
            setOpen(true);
            setMenuRect(measureAnchor());
            fetchSuggestions(text);
          }
        }}
        onBlur={(event) => {
          window.setTimeout(() => setOpen(false), 200);
          onBlur?.(event);
        }}
      />
      {menu}
    </div>
  );
}

export default OrganizationLocationInput;
